// src/pages/Collection.tsx - Enhanced with Image Caching
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ddApi } from '../lib/supabaseClient';
import { Card } from '../types';
import CardDisplay from '../components/CardDisplay';
import { useAuth } from '../context/AuthContext';
import { preloadImages, getCardImageUrls } from '../utils/imageCache';

type FilterType = 'owned' | 'all';
type CardTypeFilter = 'all' | 'offensive' | 'defensive' | 'utility';
type RarityFilter = 'all' | 'common' | 'rare' | 'legendary';
type EffectFilter = 'all' | 'basic' | 'special';
type SortOption = 'name' | 'attack' | 'type' | 'rarity';
type SortDirection = 'asc' | 'desc';

const Collection: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Card data
  const [playerCards, setPlayerCards] = useState<Card[]>([]);
  const [allCards, setAllCards] = useState<Card[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [filter, setFilter] = useState<FilterType>('owned');
  const [cardTypeFilter, setCardTypeFilter] = useState<CardTypeFilter>('all');
  const [rarityFilter, setRarityFilter] = useState<RarityFilter>('all');
  const [effectFilter, setEffectFilter] = useState<EffectFilter>('all');
  
  // Sort states
  const [sortBy, setSortBy] = useState<SortOption>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!user) return;
    loadCards();
  }, [user]);

  const loadCards = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      const [playerCardsData, allCardsData] = await Promise.all([
        ddApi.getPlayerCards(user.id),
        ddApi.getAllCards()
      ]);

      const playerCards = playerCardsData.map(pc => pc.card!);
      const allCards = allCardsData;

      setPlayerCards(playerCards);
      setAllCards(allCards);

      // SIMPLE CACHING: Preload all card images in background
      const allImageUrls = getCardImageUrls([...playerCards, ...allCards]);
      preloadImages(allImageUrls).catch(() => {
        // Silently handle preload failures - main functionality still works
      });

    } catch (error) {
      console.error('Error loading cards:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check if card has special effects (non-basic)
  const hasSpecialEffects = (card: Card): boolean => {
    const basicAttackCards = [
      'Virus Infection', 'Port Scan', 'Brute Force', 'Malware Deploy', 
      'Data Breach', 'Ransomware', 'System Takeover', 'Infrastructure Attack'
    ];
    return !basicAttackCards.includes(card.name);
  };

  // Filter and sort cards
  const getFilteredAndSortedCards = (): Card[] => {
    // Start with base card set
    let cards = filter === 'owned' ? playerCards : allCards;
    
    // Apply search filter
    if (searchTerm) {
      cards = cards.filter(card => 
        card.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        card.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply card type filter
    if (cardTypeFilter !== 'all') {
      cards = cards.filter(card => card.card_type === cardTypeFilter);
    }
    
    // Apply rarity filter
    if (rarityFilter !== 'all') {
      cards = cards.filter(card => card.rarity === rarityFilter);
    }
    
    // Apply effect filter
    if (effectFilter !== 'all') {
      if (effectFilter === 'basic') {
        cards = cards.filter(card => !hasSpecialEffects(card));
      } else if (effectFilter === 'special') {
        cards = cards.filter(card => hasSpecialEffects(card));
      }
    }
    
    // Apply sorting
    cards.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'attack':
          comparison = a.attack_value - b.attack_value;
          break;
        case 'type':
          comparison = a.card_type.localeCompare(b.card_type);
          break;
        case 'rarity':
          const rarityOrder = { common: 1, rare: 2, legendary: 3 };
          comparison = (rarityOrder[a.rarity as keyof typeof rarityOrder] || 0) - 
                      (rarityOrder[b.rarity as keyof typeof rarityOrder] || 0);
          break;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    return cards;
  };

  const displayCards = getFilteredAndSortedCards();
  const ownedCardIds = new Set(playerCards.map(card => card.id));

  // Get card counts for filter labels
  const getCardCounts = () => {
    const baseCards = filter === 'owned' ? playerCards : allCards;
    
    return {
      total: baseCards.length,
      offensive: baseCards.filter(c => c.card_type === 'offensive').length,
      defensive: baseCards.filter(c => c.card_type === 'defensive').length,
      utility: baseCards.filter(c => c.card_type === 'utility').length,
      common: baseCards.filter(c => c.rarity === 'common').length,
      rare: baseCards.filter(c => c.rarity === 'rare').length,
      legendary: baseCards.filter(c => c.rarity === 'legendary').length,
      basic: baseCards.filter(c => !hasSpecialEffects(c)).length,
      special: baseCards.filter(c => hasSpecialEffects(c)).length
    };
  };

  const counts = getCardCounts();

  // Clear all filters
  const clearFilters = () => {
    setCardTypeFilter('all');
    setRarityFilter('all');
    setEffectFilter('all');
    setSearchTerm('');
    setSortBy('name');
    setSortDirection('asc');
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 p-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-blue-400">Card Collection</h1>
            <p className="text-gray-300">
              {playerCards.length} cards unlocked • {allCards.length} total cards
            </p>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </header>

      {/* Collection Toggle */}
      <nav className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-8">
            <button
              onClick={() => setFilter('owned')}
              className={`py-4 px-2 border-b-2 font-medium text-sm ${
                filter === 'owned'
                  ? 'border-blue-500 text-blue-400'
                  : 'border-transparent text-gray-300 hover:text-white hover:border-gray-300'
              }`}
            >
              My Cards ({playerCards.length})
            </button>
            <button
              onClick={() => setFilter('all')}
              className={`py-4 px-2 border-b-2 font-medium text-sm ${
                filter === 'all'
                  ? 'border-blue-500 text-blue-400'
                  : 'border-transparent text-gray-300 hover:text-white hover:border-gray-300'
              }`}
            >
              All Cards ({allCards.length})
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto p-6">
        {/* Filters and Search */}
        <div className="bg-gray-800 rounded-lg p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Search */}
            <div className="flex-1 min-w-64">
              <input
                type="text"
                placeholder="Search cards..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
              />
            </div>
            
            {/* Card Type Filter */}
            <select
              value={cardTypeFilter}
              onChange={(e) => setCardTypeFilter(e.target.value as CardTypeFilter)}
              className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white focus:outline-none focus:border-blue-500"
            >
              <option value="all">All Types ({counts.total})</option>
              <option value="offensive">⚔️ Offensive ({counts.offensive})</option>
              <option value="defensive">🛡️ Defensive ({counts.defensive})</option>
              <option value="utility">🔧 Utility ({counts.utility})</option>
            </select>
            
            {/* Rarity Filter */}
            <select
              value={rarityFilter}
              onChange={(e) => setRarityFilter(e.target.value as RarityFilter)}
              className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white focus:outline-none focus:border-blue-500"
            >
              <option value="all">All Rarities ({counts.total})</option>
              <option value="common">Common ({counts.common})</option>
              <option value="rare">Rare ({counts.rare})</option>
              <option value="legendary">Legendary ({counts.legendary})</option>
            </select>
            
            {/* Effect Filter */}
            <select
              value={effectFilter}
              onChange={(e) => setEffectFilter(e.target.value as EffectFilter)}
              className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white focus:outline-none focus:border-blue-500"
            >
              <option value="all">All Effects ({counts.total})</option>
              <option value="basic">⚡ Basic Attacks ({counts.basic})</option>
              <option value="special">✨ Special Effects ({counts.special})</option>
            </select>
            
            {/* Sort By */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white focus:outline-none focus:border-blue-500"
            >
              <option value="name">Sort by Name</option>
              <option value="attack">Sort by Attack</option>
              <option value="type">Sort by Type</option>
              <option value="rarity">Sort by Rarity</option>
            </select>
            
            {/* Sort Direction */}
            <button
              onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
              className="bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded px-3 py-2 text-white transition-colors"
              title={`Sort ${sortDirection === 'asc' ? 'Descending' : 'Ascending'}`}
            >
              {sortDirection === 'asc' ? '↑' : '↓'}
            </button>
            
            {/* Clear Filters */}
            <button
              onClick={clearFilters}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded transition-colors"
            >
              Clear All
            </button>
          </div>
          
          {/* Active Filters Display */}
          {(cardTypeFilter !== 'all' || rarityFilter !== 'all' || effectFilter !== 'all' || searchTerm) && (
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="text-sm text-gray-400">Active filters:</span>
              {searchTerm && (
                <span className="bg-blue-600 text-white px-2 py-1 rounded text-sm">
                  Search: "{searchTerm}"
                </span>
              )}
              {cardTypeFilter !== 'all' && (
                <span className="bg-green-600 text-white px-2 py-1 rounded text-sm">
                  Type: {cardTypeFilter}
                </span>
              )}
              {rarityFilter !== 'all' && (
                <span className="bg-purple-600 text-white px-2 py-1 rounded text-sm">
                  Rarity: {rarityFilter}
                </span>
              )}
              {effectFilter !== 'all' && (
                <span className="bg-orange-600 text-white px-2 py-1 rounded text-sm">
                  Effects: {effectFilter}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Results Count */}
        <div className="mb-4">
          <p className="text-gray-400">
            Showing {displayCards.length} of {filter === 'owned' ? playerCards.length : allCards.length} cards
          </p>
        </div>

        {/* Cards Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-400">Loading collection...</div>
          </div>
        ) : displayCards.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h3 className="text-xl font-medium text-white mb-2">No cards found</h3>
            <p className="text-gray-400 mb-4">
              {searchTerm || cardTypeFilter !== 'all' || rarityFilter !== 'all' || effectFilter !== 'all'
                ? 'Try adjusting your filters or search terms'
                : 'Complete activities in other SO games to unlock cards!'
              }
            </p>
            {(searchTerm || cardTypeFilter !== 'all' || rarityFilter !== 'all' || effectFilter !== 'all') && (
              <button
                onClick={clearFilters}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {displayCards.map(card => (
              <div key={card.id} className="relative">
                <CardDisplay 
                  card={card} 
                  showDetails 
                  disabled={filter === 'all' && !ownedCardIds.has(card.id)}
                />
                {filter === 'all' && !ownedCardIds.has(card.id) && (
                  <div className="absolute inset-0 bg-black bg-opacity-75 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-red-400 text-2xl mb-2">🔒</div>
                      <div className="text-white font-medium">Locked</div>
                      <div className="text-xs text-gray-400 mt-1">
                        {card.unlock_condition.replace(/_/g, ' ')}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Special Effect Badge */}
                {hasSpecialEffects(card) && (
                  <div className="absolute top-2 left-2">
                    <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded">
                      ✨ Special
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Collection;